import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import React, { useEffect } from "react";
import { useState } from "react";
import { IconButton } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import MuiChip from "./muichip";
import { formatDate } from "../utils/utils";
import TableSortLabel from "@mui/material/TableSortLabel";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import * as BusinessConst from "../utils/businessconstants";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.light.main,
    color: "#464F60",
    fontWeight: 600,
    fontSize:10.8
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },    
  paddingRight:"0px",
  padding:"6px"
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.light.light,
  },
}));

const StyledPagination = styled(TablePagination)(({ theme }) => ({
  backgroundColor: theme.palette.light.main,
  color: theme.palette.common.black,
}));

function descendingComparator(a, b, orderBy) {
  let x =
    typeof b[orderBy] === "string" ? b[orderBy].toLowerCase() : b[orderBy];
  let y =
    typeof a[orderBy] === "string" ? a[orderBy].toLowerCase() : a[orderBy];
  if (x < y) {
    return -1;
  }
  if (x > y) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, headerCells } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headerCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={
                headCell.enableSort ? createSortHandler(headCell.id) : null
              }
            >
              {headCell.label}
              {headCell.enableSort && orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const MuiDataTable = ({ headerdata, rowData, storageKey, searchValue }) => {
  const [page, setPage] = useState(() => {
    if (storageKey) {
      return parseInt(sessionStorage.getItem(`${storageKey}_page`)) || 0;
    }
    return 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    if (storageKey) {
      return parseInt(sessionStorage.getItem(`${storageKey}_rowsPerPage`)) || 25;
    }
    return 25;
  });
  const navigate = useNavigate();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("id");
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (storageKey) {
      sessionStorage.setItem(`${storageKey}_page`, newPage);
    }
  };

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      if (searchValue != "") {
        setPage(0);
        if (storageKey) {
          sessionStorage.setItem(`${storageKey}_page`, 0);
        }
      }
    }
  }, [searchValue]);

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = Number(event.target.value) || 25;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (storageKey) {
      sessionStorage.setItem(`${storageKey}_rowsPerPage`, newRowsPerPage);
      sessionStorage.setItem(`${storageKey}_page`, 0);
    }
  };

  const handleClick = (id, pagekey) => {
    if (pagekey === BusinessConst.pagekey_userlist) {
      navigate(`/layout/viewuser/${id}`);
    } else if (pagekey === BusinessConst.pagekey_customerprofilelist) {
      navigate(`/layout/viewcustomer/${id}`);
    } else if (pagekey === BusinessConst.pagekey_shipmentlist) {
      navigate(`/layout/viewshipment/${id}`);
    }
    else if (pagekey === BusinessConst.pagekey_viewcustomshiptoparty) {
      navigate(`/layout/viewcustomshiptoparty/${id}`);
    }
  };
  const handleClickforShipmentList = (shipmentId,DeliveryId) => {
      navigate(`/layout/viewshipment/${shipmentId}/${DeliveryId}`);    
  }
  

  const visibleRows = React.useMemo(
    () =>
      stableSort(rowData, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rowData]
  );

  return (
    <Paper sx={{ width: "100%" }}>
      <TableContainer sx={{ height: "100%" , overflow: "hidden"}}>
        <Table stickyHeader aria-label="sticky table">
          <EnhancedTableHead
            headerCells={headerdata}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowData.length}
          />
          <TableBody>
            {visibleRows.map((row) => {
              return (
                <StyledTableRow hover tabIndex={-1} key={row.id}>
                      {headerdata.map((column) => {
                      const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align} sx={{ fontSize:"12px",padding:"6px"}}>
                        {(column.id === "action" && column.pagekey === BusinessConst.pagekey_shipmentlist)? (
                          <IconButton onClick={() => handleClickforShipmentList(row.shipmentDetailsId,row.shipmentDeliveryDetailsId)} 
                          sx={{ color: "#2F80ED" }}>
                            <Visibility /> </IconButton> ) 
                          :(column.id === "action")? (
                            <IconButton onClick={() => handleClick(row.id, column.pagekey)} sx={{ color: "#2F80ED" }}>
                              <Visibility /> </IconButton> ) 
                          : column.id === "isActive" || column.id === "deliveryStatus" || column.id === "podStatus" ? (
                                        <MuiChip row={row} statusKey={column.id}> </MuiChip> ) 
                          : (column.id === "lastLogin" || column.id === "createdOnDate" || column.id === "updatedOnDate")? (
                            formatDate(value) ) 
                          : column.style === "bold" ? ( <b>{value}</b> ) 
                          : ( value )
                          }
                      </TableCell>
                    );
                  })}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <StyledPagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={rowData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default MuiDataTable;
